import React, { Suspense, useState, useEffect, useCallback, useMemo } from "react";

import { Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Divider, IconButton, Typography, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import LightModeIcon from "@mui/icons-material/LightMode";
import { useLocation, useNavigate, Outlet } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import { useShortcuts } from "@/context/ShortcutContext";
import { useFullMode } from "@/hooks/useFullMode";

import type { ShortcutDefinition } from "@/context/ShortcutContext";

import { ROUTES } from "@/constants/routes";
import { MY_WORK_ITEMS, MANAGER_ITEMS, ADMIN_ITEMS, REPORT_ITEMS, PM_REPORT_ITEMS, filterNavItems } from "@/constants/navigation";
import type { NavItem } from "@/constants/navigation";
import { HELP_TEXTS, NAV_DESCRIPTIONS } from "@/constants/helpTexts";
import { CONFIG } from "@/constants/config";
import { EXTERNAL_LINKS } from "@/constants/externalLinks";

import { Loading } from "@/components/common/Loading";
import { ShortcutsOverlay } from "@/components/common/ShortcutsOverlay";
import { UserInfo } from "@/components/common/UserInfo";
import styles from "./Layout.module.scss";

interface LayoutProps {
    isDarkMode: boolean;
    onToggleTheme: () => void;
}

export const Layout: React.FC<LayoutProps> = ({ isDarkMode, onToggleTheme }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const { register } = useShortcuts();
    const isFullMode = useFullMode();
    const handleCloseShortcuts = useCallback(() => {
        setShortcutsOpen(false);
    }, []);

    const environmentTag = user?.environmentTag;
    const showManager = user?.isAdmin || user?.isProjectManager;

    const globalShortcuts = useMemo((): ShortcutDefinition[] => {
        const shortcuts: ShortcutDefinition[] = [
            {
                key: "?",
                scope: "global",
                label: "Shortcuts",
                description: "Show keyboard shortcuts",
                handler: () => setShortcutsOpen(true),
            },
            {
                key: "t",
                sequence: "g",
                scope: "global",
                label: "Go to Timesheet",
                description: "Go to Timesheet",
                handler: () => navigate(ROUTES.TIMESHEET),
            },
            {
                key: "s",
                sequence: "g",
                scope: "global",
                label: "Go to Submits",
                description: "Go to My Submits",
                handler: () => navigate(ROUTES.MY_SUBMITS),
            },
        ];
        if (showManager) {
            shortcuts.push({
                key: "a",
                sequence: "g",
                scope: "global",
                label: "Go to Approvals",
                description: "Go to Approvals",
                handler: () => navigate(ROUTES.APPROVALS),
            });
        }
        return shortcuts;
    }, [navigate, showManager]);

    useEffect(() => {
        return register(globalShortcuts);
    }, [register, globalShortcuts]);

    const drawerPaperClass = collapsed
        ? `${styles.layoutDrawerPaper} ${styles.layoutDrawerPaperCollapsed}`
        : `${styles.layoutDrawerPaper} ${styles.layoutDrawerPaperExpanded}`;

    const mainClass = collapsed ? `${styles.layoutMain} ${styles.layoutMainCollapsed}` : `${styles.layoutMain} ${styles.layoutMainExpanded}`;

    const renderNavSection = (items: NavItem[], label?: string): React.ReactNode => (
        <>
            {label && !collapsed && <Typography className={styles.layoutSectionLabel}>{label}</Typography>}
            {label && collapsed && <Divider />}
            <List className={styles.layoutNavList}>
                {items.map((item) => (
                    <Tooltip
                        key={item.path}
                        title={collapsed ? `${item.label} - ${NAV_DESCRIPTIONS[item.path] ?? ""}` : (NAV_DESCRIPTIONS[item.path] ?? "")}
                        placement="right"
                        enterDelay={CONFIG.TOOLTIP_ENTER_DELAY_MS}
                        describeChild
                    >
                        <ListItemButton
                            selected={location.pathname === item.path}
                            aria-current={location.pathname === item.path ? "page" : undefined}
                            aria-label={item.label}
                            onClick={() => navigate(item.path)}
                            className={styles.layoutNavItem}
                        >
                            <ListItemIcon className={styles.layoutNavIcon}>{item.icon}</ListItemIcon>
                            {!collapsed && <ListItemText primary={item.label} />}
                        </ListItemButton>
                    </Tooltip>
                ))}
            </List>
        </>
    );

    const showAdmin = user?.isAdmin;

    return (
        <Box className={styles.layoutRoot}>
            <a href="#main-content" className={styles.layoutSkipLink}>
                Skip to main content
            </a>
            <Drawer variant="permanent" className={styles.layoutDrawer} PaperProps={{ className: drawerPaperClass }}>
                <Box className={styles.layoutDrawerHeader}>
                    {!collapsed && (
                        <Box className={styles.layoutLogoContainer} onClick={() => navigate(ROUTES.TIMESHEET)}>
                            <img
                                src={isDarkMode ? "/brimit-logo_transparent_white.png" : "/brimit-logo_transparent_dark.png"}
                                alt="Brimit"
                                className={styles.layoutLogo}
                            />
                            <Typography className={styles.layoutTitle}>Timesheet</Typography>
                            {environmentTag && (
                                <Tooltip title={HELP_TEXTS.LAYOUT.ENVIRONMENT_TAG(environmentTag)}>
                                    <span className={styles.layoutEnvironmentTag}>{environmentTag}</span>
                                </Tooltip>
                            )}
                        </Box>
                    )}
                    <Box className={collapsed ? `${styles.layoutHeaderActions} ${styles.layoutHeaderActionsCollapsed}` : styles.layoutHeaderActions}>
                        <Tooltip title={isDarkMode ? HELP_TEXTS.LAYOUT.THEME_TO_LIGHT : HELP_TEXTS.LAYOUT.THEME_TO_DARK} placement="right">
                            <IconButton
                                onClick={onToggleTheme}
                                className={styles.layoutToggleButton}
                                aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                            >
                                {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={collapsed ? HELP_TEXTS.LAYOUT.EXPAND_SIDEBAR : HELP_TEXTS.LAYOUT.COLLAPSE_SIDEBAR} placement="right">
                            <IconButton
                                onClick={() => setCollapsed(!collapsed)}
                                className={styles.layoutToggleButton}
                                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                            >
                                {collapsed ? <MenuIcon /> : <ChevronLeftIcon />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Divider />

                <Box component="nav" aria-label="Main navigation" className={styles.layoutNavContent}>
                    {renderNavSection(filterNavItems(MY_WORK_ITEMS, isFullMode))}
                    {isFullMode && renderNavSection(REPORT_ITEMS, "Reports")}
                    {isFullMode && showManager && renderNavSection(PM_REPORT_ITEMS)}
                    {showManager && renderNavSection(filterNavItems(MANAGER_ITEMS, isFullMode), "Manager")}
                    {showAdmin && renderNavSection(filterNavItems(ADMIN_ITEMS, isFullMode), "Admin")}
                </Box>

                <Box className={styles.layoutDrawerFooter}>
                    <Divider />
                    <List className={styles.layoutFooterList}>
                        <Tooltip title={HELP_TEXTS.LAYOUT.FEEDBACK} placement="right" enterDelay={CONFIG.TOOLTIP_ENTER_DELAY_MS} describeChild>
                            <ListItemButton
                                component="a"
                                href={EXTERNAL_LINKS.FEEDBACK_FORM}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Send feedback (opens in a new tab)"
                                className={styles.layoutNavItem}
                            >
                                <ListItemIcon className={styles.layoutNavIcon}>
                                    <FeedbackOutlinedIcon />
                                </ListItemIcon>
                                {!collapsed && <ListItemText primary="Send feedback" />}
                            </ListItemButton>
                        </Tooltip>
                    </List>
                    <Divider />
                    <Box className={styles.layoutUserSection}>
                        <Tooltip title={HELP_TEXTS.LAYOUT.PROFILE} placement="right" enterDelay={CONFIG.TOOLTIP_ENTER_DELAY_MS} describeChild>
                            <ListItemButton
                                selected={location.pathname === ROUTES.PROFILE}
                                aria-current={location.pathname === ROUTES.PROFILE ? "page" : undefined}
                                onClick={() => navigate(ROUTES.PROFILE)}
                                className={styles.layoutNavItem}
                            >
                                <UserInfo name={user?.name ?? "User"} size={collapsed ? "small" : "medium"} showEmail={!collapsed} />
                            </ListItemButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Drawer>

            <Box component="main" id="main-content" className={mainClass}>
                <Suspense key={location.pathname} fallback={<Loading />}>
                    <Outlet context={{ isDarkMode, onToggleTheme, openShortcuts: () => setShortcutsOpen(true) }} />
                </Suspense>
            </Box>

            <ShortcutsOverlay open={shortcutsOpen} onClose={handleCloseShortcuts} />
        </Box>
    );
};
